import React from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { commonStyles } from "../../../../common/commonStyles";
import { applyCmpOrganizationNoun } from "../../../../../utils/cmpOrganizationNoun";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import {
  isEveryPermissionSelected,
  isPermissionGroupChecked,
  isPermissionGroupIndeterminate,
  isPermissionSelectionIndeterminate,
  PERMISSION_GROUPS,
  selectAllPermissions,
  togglePermissionGroup,
  togglePermissionLeaf,
} from "./permissionCatalogue";
import { PermissionSelection } from "./types";

export type PermissionMatrixProps = {
  value: PermissionSelection;
  onChange: (value: PermissionSelection) => void;
  disabled?: boolean;
  error?: string;
};

// 7px keeps the 18px box where the earlier 24px icon drew it.
const checkboxSx = {
  p: "7px",
};

export const PermissionMatrix = ({
  value,
  onChange,
  disabled = false,
  error,
}: PermissionMatrixProps) => {
  const noun = useCmpOrganizationNoun();
  const allSelected = isEveryPermissionSelected(value);
  const someSelected = isPermissionSelectionIndeterminate(value);

  return (
    <fieldset
      aria-describedby={error ? "staff-permissions-error" : undefined}
      className={`rounded-lg border bg-white p-4 sm:p-5 ${
        error ? "border-red-400" : "border-gray-200"
      }`}
      disabled={disabled}
    >
      <legend className="sr-only">Staff dashboard permissions</legend>
      <div className="mb-4 flex flex-col gap-3 border-b border-gray-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-extrabold text-gray-950">
            Dashboard access
          </p>
          <p className="mt-1 text-sm leading-5 text-gray-600">
            Module checkboxes select every page in that module. Only page-level
            permissions are saved.
          </p>
        </div>
        <Button
          type="button"
          variant="outlined"
          aria-pressed={allSelected}
          disabled={disabled}
          onClick={() => onChange(selectAllPermissions(!allSelected))}
          startIcon={
            allSelected ? (
              <CheckBoxIcon aria-hidden="true" />
            ) : someSelected ? (
              <IndeterminateCheckBoxIcon aria-hidden="true" />
            ) : (
              <CheckBoxOutlineBlankIcon aria-hidden="true" />
            )
          }
          sx={{
            alignSelf: { xs: "stretch", sm: "center" },
            borderColor: "#CBD5E1",
            color: "#111827",
            fontWeight: 800,
            minWidth: 138,
            textTransform: "none",
            "& .MuiButton-startIcon": {
              color:
                allSelected || someSelected
                  ? commonStyles.cpaGold
                  : "#CBD5E1",
            },
            "&:hover": {
              backgroundColor: "#FFFBEB",
              borderColor: commonStyles.cpaGold,
            },
          }}
        >
          {allSelected ? "Clear All" : "Select All"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        {PERMISSION_GROUPS.map((group) => {
          const checked = isPermissionGroupChecked(value, group);
          const indeterminate = isPermissionGroupIndeterminate(value, group);

          return (
            <section
              key={group.id}
              aria-labelledby={`${group.id}-permission-heading`}
              className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={({ target }) =>
                      onChange(
                        togglePermissionGroup({
                          checked: target.checked,
                          group,
                          selection: value,
                        })
                      )
                    }
                    sx={checkboxSx}
                  />
                }
                label={
                  <span
                    id={`${group.id}-permission-heading`}
                    className="text-sm font-extrabold text-gray-950"
                  >
                    {applyCmpOrganizationNoun(group.label, noun)}
                  </span>
                }
                sx={{
                  alignItems: "center",
                  m: 0,
                  width: "100%",
                }}
              />

              <div className="mt-2 space-y-1 border-l-2 border-gray-200 pl-3">
                {group.permissions.map((permission) => (
                  <div key={permission.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={value[group.platform].includes(
                            permission.value
                          )}
                          onChange={({ target }) =>
                            onChange(
                              togglePermissionLeaf({
                                checked: target.checked,
                                permission: permission.value,
                                platform: group.platform,
                                selection: value,
                              })
                            )
                          }
                          sx={checkboxSx}
                        />
                      }
                      label={
                        <span className="text-sm leading-5 text-gray-800">
                          {applyCmpOrganizationNoun(permission.label, noun)}
                        </span>
                      }
                      sx={{
                        alignItems: "flex-start",
                        m: 0,
                        width: "100%",
                      }}
                    />
                    {permission.description ? (
                      <p className="-mt-1 mb-2 ml-8 text-xs leading-5 text-gray-500">
                        {applyCmpOrganizationNoun(permission.description, noun)}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {error ? (
        <FormHelperText id="staff-permissions-error" error sx={{ mt: 1.5 }}>
          {error}
        </FormHelperText>
      ) : null}
    </fieldset>
  );
};

export default PermissionMatrix;
